import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePayHeadsEntry } from './hooks/usePayHeadsEntry';
import AppDropdown from '../../../components/AppDropdown';
import AppCombobox from '../../../components/AppCombobox';
import { appToast } from '../../../components/AppToast';
import FormulaBuilderSection from './components/FormulaBuilderSection';
import {
  ACCOUNT_HEAD_OPTIONS,
  PAY_HEAD_TYPE_OPTIONS,
  CATEGORY_OPTIONS,
  INCOME_TYPE_OPTIONS,
  CALCULATION_TYPE_OPTIONS,
  ATTENDANCE_LEAVE_WITH_PAY_OPTIONS,
  CALCULATION_PERIOD_TYPE_OPTIONS,
  BASIS_OF_CALC_PER_DAY_OPTIONS,
  YES_NO_OPTIONS,
} from './api/payHeadsApi';

export default function PayHeadsEntryScreen() {
  const { state, actions } = usePayHeadsEntry();
  const navigate = useNavigate();
  const { form, availablePayHeads, isEdit, isLoading, isSaving } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1100px] flex-col items-center justify-center p-20">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Pay Head Details...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-6 pb-16">
      {/* Breadcrumb Navigation */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-primary">
          <span>Payroll</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span>Compensation &amp; Loan</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <Link to="/payroll/payheads" className="hover:underline">
            Pay Heads
          </Link>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-on-surface-variant font-bold">{isEdit ? 'Edit' : 'Create Entry'}</span>
        </div>

        <Link
          to="/payroll/payheads"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-on-surface-variant hover:text-primary transition self-start sm:self-auto"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* Main Pay Heads Form Card - Exactly matching Legacy Screen */}
      <section className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs overflow-hidden">
        {/* Card Header matching legacy banner */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-3.5">
          <h2 className="font-display text-lg font-bold text-on-surface tracking-tight">Pay Heads</h2>
          <button
            type="button"
            title="Back to Pay Heads"
            onClick={() => navigate('/payroll/payheads')}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition"
          >
            <span className="material-symbols-outlined text-lg">undo</span>
          </button>
        </div>

        {/* Form Body - Sequential 1:1 legacy fields */}
        <div className="p-8">
          <div className="max-w-xl mx-auto space-y-3.5">
            {/* 1. Account Head */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Account Head
              </label>
              <div className="flex items-center gap-1.5">
                <div className="flex-1">
                  <AppCombobox
                    options={state.accountHeadOptions && state.accountHeadOptions.length > 0 ? state.accountHeadOptions : ACCOUNT_HEAD_OPTIONS}
                    value={form.accountHead}
                    onChange={(val) => actions.handleAccountHeadChange(val)}
                    placeholder="Select Account Head..."
                    searchPlaceholder="Search account head..."
                    triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                  />
                </div>
                <button
                  type="button"
                  title="Account head info"
                  onClick={() => appToast.info('Account Heads link this payroll component to your General Ledger Chart of Accounts (AcHeads Master).')}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:text-primary hover:bg-surface-container-high transition cursor-pointer"
                >
                  <span className="material-symbols-outlined text-base">info</span>
                </button>
                <button
                  type="button"
                  title="Create new Account Head"
                  onClick={() => navigate('/masters/acheads/heads/entry')}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:text-primary hover:bg-surface-container-high transition cursor-pointer"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                </button>
              </div>
            </div>

            {/* 2. Pay Head Name */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Pay Head Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => actions.patchForm('name', e.target.value)}
                placeholder="e.g. Basic Salary"
                className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs font-bold text-on-surface outline-none placeholder:font-normal placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>

            {/* 3. PaySlip Desc */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-start gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right pt-2">
                PaySlip Desc
              </label>
              <textarea
                rows={3}
                value={form.payslipDesc}
                onChange={(e) => actions.patchForm('payslipDesc', e.target.value)}
                placeholder="Description to appear on employee pay slips..."
                className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-low p-3 text-xs text-on-surface outline-none placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-y"
              />
            </div>

            {/* 4. Pay Head Type */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Pay Head Type
              </label>
              <AppDropdown
                options={state.dynamicOptions?.payHeadTypes?.length ? state.dynamicOptions.payHeadTypes : PAY_HEAD_TYPE_OPTIONS}
                value={form.payHeadType}
                onChange={(val) => actions.patchForm('payHeadType', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 5. Category */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Category
              </label>
              <AppDropdown
                options={state.dynamicOptions?.categories?.length ? state.dynamicOptions.categories : CATEGORY_OPTIONS}
                value={form.category}
                onChange={(val) => actions.patchForm('category', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 6. Income Type */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Income Type
              </label>
              <AppDropdown
                options={state.dynamicOptions?.incomeTypes?.length ? state.dynamicOptions.incomeTypes : INCOME_TYPE_OPTIONS}
                value={form.incomeType}
                onChange={(val) => actions.patchForm('incomeType', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 7. Calculation Type */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Calculation Type
              </label>
              <AppDropdown
                options={state.dynamicOptions?.calculationTypes?.length ? state.dynamicOptions.calculationTypes : CALCULATION_TYPE_OPTIONS}
                value={form.calculationType}
                onChange={(val) => actions.patchForm('calculationType', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 8. Attendance Leave with Pay - Always visible matching legacy */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Attendance Leave with Pay
              </label>
              <AppDropdown
                options={state.dynamicOptions?.attendanceLeaveWithPay?.length ? state.dynamicOptions.attendanceLeaveWithPay : ATTENDANCE_LEAVE_WITH_PAY_OPTIONS}
                value={form.attendanceLeaveWithPay}
                onChange={(val) => actions.patchForm('attendanceLeaveWithPay', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 9. Calculation Period Type */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Calculation Period Type
              </label>
              <AppDropdown
                options={state.dynamicOptions?.calculationPeriods?.length ? state.dynamicOptions.calculationPeriods : CALCULATION_PERIOD_TYPE_OPTIONS}
                value={form.calculationPeriodType}
                onChange={(val) => actions.patchForm('calculationPeriodType', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 10. Basis of Calculation Per Day */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Basis of Calculation Per Day
              </label>
              <AppDropdown
                options={state.dynamicOptions?.basisOfCalcPerDay?.length ? state.dynamicOptions.basisOfCalcPerDay : BASIS_OF_CALC_PER_DAY_OPTIONS}
                value={form.basisOfCalcPerDay}
                onChange={(val) => actions.patchForm('basisOfCalcPerDay', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 11. User Defined Days - Always visible matching legacy */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                User Defined Days
              </label>
              <input
                type="number"
                min="0"
                value={form.userDefinedDays}
                onChange={(e) => actions.patchForm('userDefinedDays', Number(e.target.value))}
                className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>

            {/* 12. Include in Salary Slip */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Include in Salary Slip
              </label>
              <AppDropdown
                options={YES_NO_OPTIONS}
                value={form.includeInSalarySlip}
                onChange={(val) => actions.patchForm('includeInSalarySlip', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>

            {/* 13. Effect Net Salary */}
            <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] items-center gap-3">
              <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                Effect Net Salary
              </label>
              <AppDropdown
                options={YES_NO_OPTIONS}
                value={form.effectNetSalary}
                onChange={(val) => actions.patchForm('effectNetSalary', val)}
                triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
              />
            </div>
          </div>

          {/* Conditional Formula / Slabs Builder Section when Computed Value is chosen */}
          {form.calculationType === 'As Computed Value' && (
            <div className="mt-8 border-t border-outline-variant/20 pt-6 max-w-2xl mx-auto">
              <h3 className="text-xs font-black uppercase tracking-[0.12em] text-primary mb-4">
                Formula Dependencies &amp; Progressive Slabs
              </h3>
              <FormulaBuilderSection
                availablePayHeads={availablePayHeads}
                percentage={form.percentage}
                onPercentageChange={(val) => actions.patchForm('percentage', val)}
                formulas={form.formulas}
                onFormulasChange={(val) => actions.patchForm('formulas', val)}
                slabs={form.slabs}
                onSlabsChange={(val) => actions.patchForm('slabs', val)}
              />
            </div>
          )}
        </div>
      </section>

      {/* Sticky Bottom Actions Bar - Exact matching Legacy Buttons */}
      <section className="flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-4 shadow-sm">
        <button
          type="button"
          onClick={() => actions.handleSave({ exitAfter: true })}
          disabled={isSaving}
          className="rounded-xl bg-[#287ab9] px-7 py-2 text-xs font-bold text-white shadow-sm transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          {isSaving ? 'saving...' : 'save & eXit'}
        </button>

        {!isEdit && (
          <button
            type="button"
            disabled={isSaving}
            onClick={() => actions.handleSave({ exitAfter: false })}
            className="rounded-xl border border-outline-variant/50 bg-white px-6 py-2 text-xs font-bold text-on-surface shadow-xs transition hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            Save &amp; continue
          </button>
        )}

        <button
          type="button"
          onClick={() => navigate('/payroll/payheads')}
          className="rounded-xl border border-outline-variant/40 bg-white px-6 py-2 text-xs font-bold text-on-surface-variant shadow-xs transition hover:bg-surface-container-low cursor-pointer"
        >
          Cancel
        </button>
      </section>
    </div>
  );
}
